// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { useConfig } from '../hooks/other/useConfig';
import LedgerApiClientV2 from '../utils/LedgerApiClientV2';
import { ACSHelper } from '../utils/ACSHelper';
import { useJsonApiClient } from './JsonApiContext';
import { useWSSupport } from './WSSupportContext';
import { LedgerApiContextV2 } from './LedgerApiContext';
import { useModuleParty } from './ModuleContext';
import { useUserState } from './UserProvider';

export const LedgerApiClientProvider = ({ children }: React.PropsWithChildren) => {
  const config = useConfig();
  const party = useModuleParty();
  const { loggedInUser } = useUserState();
  const jsonApi = useJsonApiClient();
  const wsSupport = useWSSupport();

  const [ledgerApiClientV2, setLedgerApiClientV2] = useState<LedgerApiClientV2 | undefined>(
    undefined,
  );

  useEffect(() => {
    if (loggedInUser) {
      // Create ACS helper with websocket support
      const acsHelper = new ACSHelper(jsonApi, wsSupport);
      // client for JSON API v2
      const apiClientV2 = new LedgerApiClientV2(jsonApi, loggedInUser.userId, party, 30, acsHelper);
      setLedgerApiClientV2(apiClientV2);
    }
  }, [jsonApi, loggedInUser, party, config, wsSupport]);

  if (!ledgerApiClientV2) {
    return <Loading text="Initializing ledger client" />;
  }

  return (
    <LedgerApiContextV2.Provider value={ledgerApiClientV2}>{children}</LedgerApiContextV2.Provider>
  );
};
